import { InputHTMLAttributes, forwardRef, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'suffix'> {
  label?: string
  error?: string
  className?: string
  prefix?: ReactNode
  suffix?: ReactNode
  optional?: boolean
  optionalText?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, disabled, className = '', prefix, suffix, optional, optionalText, ...props }, ref) => {
    const { t } = useTranslation();
    return (
      <div className="relative flex flex-col">
        {label && (
          <label className="text-sm text-zinc-700 mb-2">
            {label}
            {optional && <span className="text-xs text-zinc-500 ml-1">({optionalText || t('common.optional')})</span>}
          </label>
        )}
        <div className='relative'>
          {prefix}
          <input
            ref={ref}
            disabled={disabled}
            className={`
            w-full
            px-4 
            ${suffix ? 'pr-10' : ''}
            py-3 
            rounded-xl
            outline-hidden
            text-base
            text-zinc-700
            transition-all
            duration-200
            ease-in-out
            ${disabled ?
                'bg-gray-100 cursor-not-allowed' :
                'border border-gray-200 hover:border-gray-300 focus:border-blue-600 focus:ring-4 focus:ring-blue-200 focus:text-black'
              }
            ${error ? 'border-red-500' : ''}
            ${className}
          `}
            {...props}
          />
          {suffix}
        </div>
        {error && (
          <motion.span
            key={error}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-sm text-red-500 mt-3">
            {error}
          </motion.span>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input